import { useHead } from "../../stream/useHead.ts";
import s from "./shell.module.css";

/** THE POSITION THE SCREEN IS CURRENT AS OF, and whether that position is HEALTHY. Rendered in the top bar
 *  for the app's whole lifetime.
 *
 *  It is the app's only always-on instrument, and it is built on the one rule that matters here: **a screen
 *  that has gone quiet and a market that has gone quiet look identical**, so every state below is named.
 *
 *    connecting/reconnecting — the transport. NOT "0 slots behind": a null frontier rendered as a number is a
 *                 lie that looks like data, and every store on this surface treats null and zero as different
 *                 facts.
 *    idle       — the socket CLOSED because no keyed view is subscribed (the rule exists so a page with
 *                 nothing on it stops reconnecting forever). The last position is still true as of when it
 *                 was read, and it is no longer advancing — so it is labelled rather than left ticking.
 *    reload     — an incompatible wire version. The server will never speak `v1` again, so reconnecting would
 *                 turn one deploy into a storm; the client stops and says what to do.
 *    base       — the durable base with NO overlay, ~12–14 s stale. Every seed taken in this tier is
 *                 PROVISIONAL, and every open view re-seeds on the transition to `processed`.
 *    stalled    — `head.stalledSec` is the SERVER's measurement of how long `second` has not advanced. The
 *                 browser cannot compute it: the only clock it holds comparable to `asOf.second` IS
 *                 `asOf.second`, and subtracting a wall clock reports the healthy 3–12 s block-time lag as
 *                 staleness.
 *    tip        — live. Quiet, which is the point.
 *
 *  This component is also what makes the socket's idle-close rule REACHABLE: it holds a status handler for the
 *  app's lifetime, and the rule is scoped to KEYED topics precisely so this one does not keep a socket with no
 *  subscriptions alive forever. */

/** Above this many seconds without the chain second advancing, the tip is not a tip. It is the server's own
 *  number, and the threshold is generous: at 2.5 slots/s a healthy frontier advances several times a second,
 *  so 15 s is ~40 missed slots and not a rounding error. */
const STALLED_SEC = 15;

export function AsOfBadge() {
  const { asOf, head, socket, counters } = useHead();

  // The transport's own states come first: they are statements about whether the number below can be trusted
  // to still be moving, and a stale-but-labelled number beats a fresh-looking one.
  if (socket === "incompatible") {
    return (
      <span
        className={s.badge}
        title="The server speaks a wire version this build does not. Reload."
      >
        <span className={[s.dot, s.dotStale].join(" ")} />
        reload required
      </span>
    );
  }

  if (asOf === null) {
    return (
      <span
        className={s.badge}
        title="No head frame yet — the socket has not delivered a position."
      >
        <span className={s.dot} />
        {socket === "reconnecting" ? "reconnecting" : "connecting"}
      </span>
    );
  }

  const stalled = head !== null && head.stalledSec >= STALLED_SEC;
  const base = asOf.tier === "base";
  // `closed` here is the IDLE rule having fired, not a failure: no keyed view is subscribed, so there is
  // nothing for the connection to carry.
  const idle = socket === "closed";
  const dot = idle || stalled ? s.dotStale : base ? s.dotBase : s.dotLive;

  const label = idle
    ? `idle · slot ${asOf.slot}`
    : socket === "reconnecting"
      ? `reconnecting · slot ${asOf.slot}`
      : stalled
        ? `stalled ${(head as { stalledSec: number }).stalledSec}s`
        : base
          ? "base tier"
          : `tip · slot ${asOf.slot}`;

  return (
    <span className={s.badge} title={tooltip(asOf, head, socket, counters)}>
      <span className={[s.dot, dot].join(" ")} />
      {label}
    </span>
  );
}

/** Everything an operator would ask for, in the one place they will hover. The counters are READ OFF THE
 *  SOCKET rather than tallied again: a repair that is invisible cannot be trusted, and a second tally is a
 *  second number to disagree with. `behind` is `null` when no independent RPC tip is configured, and it
 *  renders as "unknown" rather than as 0 — it must NEVER be derived from our own cursor, which is circular. */
function tooltip(
  asOf: { slot: number; second: number; finalizedSlot: number; tier: string },
  head: { behind: number | null; stalledSec: number; shedLevel: number } | null,
  socket: string,
  counters: { frames: number; gaps: number; reconnects: number; refusals: number; dropped: number },
): string {
  const lines = [
    `socket ${socket}`,
    `tier ${asOf.tier}`,
    `slot ${asOf.slot} · finalized ${asOf.finalizedSlot}`,
    `chain second ${asOf.second}`,
  ];
  if (head !== null) {
    lines.push(`behind ${head.behind === null ? "unknown" : `${head.behind} slots`}`);
    lines.push(`stalled ${head.stalledSec}s · shed level ${head.shedLevel}`);
  }
  lines.push(
    `frames ${counters.frames} · gaps ${counters.gaps} · reconnects ${counters.reconnects} · refused ${counters.refusals} · dropped ${counters.dropped}`,
  );
  return lines.join("\n");
}
